import { FormEvent, useEffect, useMemo, useState } from "react";
import { Modal } from "@/components/ui/Modal";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { Button } from "@/components/ui/Button";
import type { FolhaServicoDto } from "@/services/folhaServicos";
import type {
  FolhaLancamentoDto,
  FolhaLancamentoFormValues,
} from "@/services/folhaLancamentos";

const FUNCAO_OPTIONS = [
  { value: "Pedreiro", label: "Pedreiro" },
  { value: "OP.Betoneira", label: "OP.Betoneira" },
  { value: "Servente", label: "Servente" },
  { value: "Eletricista", label: "Eletricista" },
  { value: "Porteiro", label: "Porteiro" },
];

interface ModalLancamentoFolhaProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: FolhaLancamentoFormValues) => Promise<void>;
  servicosCatalogo: FolhaServicoDto[];
  lancamento?: FolhaLancamentoDto | null;
}

export function ModalLancamentoFolha({
  open,
  onClose,
  onSave,
  servicosCatalogo,
  lancamento = null,
}: ModalLancamentoFolhaProps) {
  const [quinzena, setQuinzena] = useState("");
  const [funcao, setFuncao] = useState("");
  const [colaborador, setColaborador] = useState("");
  const [casa, setCasa] = useState("");
  const [servicoId, setServicoId] = useState("");
  const [valorPagamento, setValorPagamento] = useState("");
  const [valorDoServico, setValorDoServico] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const editing = Boolean(lancamento);

  const servicoOptions = useMemo(
    () =>
      servicosCatalogo.map((servico) => ({
        value: String(servico.id),
        label: `${servico.nome} — ${new Intl.NumberFormat("pt-BR", {
          style: "currency",
          currency: "BRL",
        }).format(servico.valorUnitario)}`,
      })),
    [servicosCatalogo],
  );

  const selectedServicoId = servicoId ? Number(servicoId) : null;
  const selectedServico =
    selectedServicoId != null
      ? servicosCatalogo.find((item) => item.id === selectedServicoId) ?? null
      : null;
  const valorPagamentoNum = Number(valorPagamento);
  const valorDoServicoNum = Number(valorDoServico);
  const saldoPreview =
    Number.isFinite(valorPagamentoNum) && Number.isFinite(valorDoServicoNum)
      ? Math.max(0, Number((valorDoServicoNum - valorPagamentoNum).toFixed(2)))
      : null;

  useEffect(() => {
    if (!open) return;
    setQuinzena(lancamento?.quinzena ?? "");
    setFuncao(lancamento?.funcao ?? "");
    setColaborador(lancamento?.colaborador ?? "");
    setCasa(lancamento?.casa ?? "");
    setServicoId(lancamento?.servicoId != null ? String(lancamento.servicoId) : "");
    setValorPagamento(lancamento != null ? String(lancamento.valorPagamento) : "");
    setValorDoServico(lancamento != null ? String(lancamento.valorDoServico) : "");
    setError("");
    setSaving(false);
  }, [open, lancamento]);

  function handleServicoChange(nextId: string) {
    setServicoId(nextId);
    setError("");
    const catalog = servicosCatalogo.find((item) => item.id === Number(nextId));
    if (catalog) setValorDoServico(String(catalog.valorUnitario));
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const pagamento = Number(valorPagamento);
    const valorServico = Number(valorDoServico);

    if (!quinzena.trim()) {
      setError("Informe a quinzena.");
      return;
    }
    if (!funcao.trim()) {
      setError("Informe a função.");
      return;
    }
    if (!colaborador.trim()) {
      setError("Informe o colaborador.");
      return;
    }
    if (!casa.trim()) {
      setError("Informe a casa.");
      return;
    }
    if (!servicoId && !selectedServico) {
      setError("Selecione o serviço.");
      return;
    }
    if (!Number.isFinite(pagamento) || pagamento < 0) {
      setError("Informe um valor de pagamento válido.");
      return;
    }
    if (!Number.isFinite(valorServico) || valorServico < 0) {
      setError("Informe o valor do serviço.");
      return;
    }

    const servicoNome =
      selectedServico?.nome ??
      servicosCatalogo.find((item) => item.id === selectedServicoId)?.nome ??
      lancamento?.servicoNome ??
      "";

    if (!servicoNome) {
      setError("Selecione o serviço.");
      return;
    }

    setSaving(true);
    setError("");
    try {
      await onSave({
        quinzena: quinzena.trim(),
        funcao: funcao.trim(),
        colaborador: colaborador.trim(),
        casa: casa.trim(),
        servicoId: selectedServicoId,
        servicoNome,
        valorPagamento: pagamento,
        valorDoServico: valorServico,
      });
      onClose();
    } catch {
      setError("Não foi possível salvar o lançamento.");
    } finally {
      setSaving(false);
    }
  }

  function handleClose() {
    if (!saving) onClose();
  }

  const currency = new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  });

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={editing ? "Editar serviço na folha" : "Cadastrar serviço na folha"}
      size="lg"
      closeOnEscape
      footer={
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={handleClose} disabled={saving}>
            Cancelar
          </Button>
          <Button type="submit" form="folha-lancamento-form" loading={saving}>
            {editing ? "Salvar alterações" : "Cadastrar serviço"}
          </Button>
        </div>
      }
    >
      <form
        id="folha-lancamento-form"
        className="grid gap-4 sm:grid-cols-2"
        onSubmit={(e) => void handleSubmit(e)}
      >
        {error && (
          <div
            role="alert"
            className="sm:col-span-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
          >
            {error}
          </div>
        )}

        <Input
          label="Quinzena"
          value={quinzena}
          onChange={(event) => {
            setQuinzena(event.target.value);
            setError("");
          }}
          placeholder="Ex.: 2º - QZ-AGOSTO-2024"
          required
          autoFocus
        />

        <Select
          label="Função"
          options={FUNCAO_OPTIONS}
          value={funcao}
          onChange={(event) => {
            setFuncao(event.target.value);
            setError("");
          }}
          required
        />

        <Input
          label="Colaborador"
          value={colaborador}
          onChange={(event) => {
            setColaborador(event.target.value);
            setError("");
          }}
          required
        />

        <Input
          label="Casa"
          value={casa}
          onChange={(event) => {
            setCasa(event.target.value);
            setError("");
          }}
          placeholder="Ex.: 25 ou Geral"
          required
        />

        <div className="sm:col-span-2">
          <SearchableSelect
            label="Serviço"
            options={servicoOptions}
            value={servicoId}
            onChange={handleServicoChange}
            placeholder="Buscar serviço cadastrado…"
          />
        </div>

        <Input
          label="Valor pagamento"
          type="number"
          inputMode="decimal"
          min={0}
          step="0.01"
          value={valorPagamento}
          onChange={(event) => {
            setValorPagamento(event.target.value);
            setError("");
          }}
          required
        />

        <Input
          label="Valor do serviço"
          type="number"
          inputMode="decimal"
          min={0}
          step="0.01"
          value={valorDoServico}
          onChange={(event) => {
            setValorDoServico(event.target.value);
            setError("");
          }}
          required
        />

        <div className="sm:col-span-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600">
          <span className="font-medium text-slate-700">Saldo: </span>
          {saldoPreview == null ? "—" : currency.format(saldoPreview)}
          <span className="ml-2 text-xs text-slate-400">
            (Valor do serviço − Valor pagamento)
          </span>
        </div>
      </form>
    </Modal>
  );
}
